import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

export type NetworkStatus = 'online' | 'offline';

export function useNetworkStatus() {
  // On Android WebView, navigator.onLine is unreliable at startup (returns false
  // before the WebView finishes loading). Default to 'online' on native platforms
  // and let Capacitor Network.getStatus() correct it if truly offline.
  const [status, setStatus] = useState<NetworkStatus>(
    Capacitor.isNativePlatform() ? 'online' : (navigator.onLine ? 'online' : 'offline')
  );

  useEffect(() => {
    const handleOffline = () => setStatus('offline');
    const handleOnline  = () => setStatus('online');

    if (Capacitor.isNativePlatform()) {
      import('@capacitor/network')
        .then(({ Network }) => {
          Network.getStatus().then((s) => {
            setStatus(s.connected ? 'online' : 'offline');
          });
          Network.addListener('networkStatusChange', (s) => {
            setStatus(s.connected ? 'online' : 'offline');
          });
        })
        .catch(() => {
          globalThis.addEventListener('offline', handleOffline);
          globalThis.addEventListener('online', handleOnline);
        });
    } else {
      globalThis.addEventListener('offline', handleOffline);
      globalThis.addEventListener('online', handleOnline);
    }

    return () => {
      globalThis.removeEventListener('offline', handleOffline);
      globalThis.removeEventListener('online', handleOnline);
      if (Capacitor.isNativePlatform()) {
        import('@capacitor/network')
          .then(({ Network }) => Network.removeAllListeners())
          .catch(() => {});
      }
    };
  }, []);

  return status;
}
